import { Box, Container, Modal } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { COLOR_HOME } from '../../routes/color-constants'
import { RouteProps } from '../../routes/AppRouter'
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Actions, Field, Pager, Search, SearchValue, Sort } from '../../components/table/types'
import { AppTable } from '../../components/table'
import { SortParam } from '../../common/api/Query'
import deleteIcon from '../../assets/table-icons/delete-icon.svg'
import { Permission } from '../../common/enums/Permissions'
import { getAuthContainer } from '../../container/auth-modules'
import { AuthService } from '../../modules/auth/services/AuthService'
import { AUTH_SERVICE_KEY } from '../../modules/auth'
import { getClient2RequestContainer } from "../../container/client2request-modules"
import { Client2RequestService } from "../../modules/client2Requests/services/Client2RequestService"
import { CLIENT2REQUEST_SERVICE_KEY } from "../../modules/client2Requests"
import {Client2RequestQuery } from "../../modules/client2Requests/models/Client2Request"
import { getRequestContainer } from 'container/request-modules'
import { RequestService } from 'modules/request/services/RequestService'
import { REQUEST_SERVICE_KEY } from 'modules/request'
import { RequestView } from 'modules/client2Requests/models/RequestsView'
import { LoadingSpinner } from 'components/loading-spinner/LoadingSpinner'
import style from '../../components/loading-spinner/LoadingSpinner.module.css'
import { FormAction, FormActions } from 'common/utils/form-generation'
import { commonStyles } from '../../common/styles/Styles'



type DetailProps = {
    id?: string
}

const authService = getAuthContainer().get<AuthService>(AUTH_SERVICE_KEY)
const client2RequestService = getClient2RequestContainer().get<Client2RequestService>(CLIENT2REQUEST_SERVICE_KEY)
const requestService = getRequestContainer().get<RequestService>(REQUEST_SERVICE_KEY)

export const ClientApplication = (props: DetailProps) => {
    return (
        <Container>
            <PatientTests {...props} />
        </Container>
    )
}

export const PatientTests = (props: RouteProps) => {
    const { t } = useTranslation()
      const styles = commonStyles()
    
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [page, setPage] = useState<number>(0)
    const [itemsPerPage, setItemsPerPage] = useState<number>(10)
    const [allItems, setAllItems] = useState<RequestView[]>([])
    const [counter, setCounter] = useState<number>(0)
    const [sort, setSort] = useState<SortParam<RequestView>>({
        field: 'createdAt',
        desc: true,
    })
    const [searchQuery, setSearchQuery] = useState<string>('')
    const [modalDelete, setModalDelete] = useState<boolean>(false)
    const [toDelete, setToDelete] = useState<RequestView>()

    useEffect(() => {
        setIsLoading(true)
        client2RequestService.getByClientID(props.id || '').subscribe((res) => {
            setAllItems(res.items)
            setCounter(res.count)
            setIsLoading(false)
        })
    }, [props.id])

    const filteredItems = useMemo(() => {
        if (!searchQuery) return allItems
        
        return allItems.filter((item) => 
            item.name.toUpperCase().includes(searchQuery.toUpperCase())
        )
    }, [allItems, searchQuery])

    const sortedItems = useMemo(() => {
        const items = [...filteredItems]
        const { field, desc } = sort

        if (field === 'date') {
            return items.sort((a, b) => {
                const timeA = new Date(a.date).getTime()
                const timeB = new Date(b.date).getTime()
                return desc ? timeB - timeA : timeA - timeB
            })
        } else if (field === 'state') {
            return items.sort((a, b) => {
                const statusA = t(a.status)
                const statusB = t(b.status)
                return desc 
                    ? statusB.localeCompare(statusA)
                    : statusA.localeCompare(statusB)
            })
        }

        return items
    }, [filteredItems, sort, t])

    const paginatedItems = useMemo(() => {
        const start = page * itemsPerPage
        return sortedItems.slice(start, start + itemsPerPage)
    }, [sortedItems, page, itemsPerPage])

    const count = counter

      const actionsDelete: FormAction[] = [
        {
          label: t('close'),
          handleAction: ()=>setModalDelete(false),
        },
      ]

    const handlePaginationChange = useCallback((event: unknown, value: number) => {
        setPage(value)
    }, [])

    const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = Number.parseInt(event.target.value)
        setItemsPerPage(Number.isNaN(value) ? 10 : value)
        setPage(0)
    }, [])

    const handleSort = useCallback((field: keyof RequestView) => {
        setSort(prevSort => ({
            field,
            desc: prevSort.field === field ? !prevSort.desc : true
        }))
    }, [])

    const handleSearch = useCallback((svs: SearchValue<Client2RequestQuery>[]) => {
        setSearchQuery(svs[0]?.value?.toString() || '')
        setPage(0)
    }, [])

    const deleteTest = useCallback((r: RequestView) => {
        setIsLoading(true)
        requestService.delete(r.id).subscribe(() => {
            setAllItems(prev => prev.filter(item => item.id !== r.id))
            setIsLoading(false)
        })
    }, [])

    const showDeleteButton = useCallback(() => {
        return !authService.userCan(Permission.deletePatientTests)
    }, [])

    const pager = useMemo<Pager>(() => ({
        page,
        count,
        handleChangePage: handlePaginationChange,
        rowsPerPage: itemsPerPage,
        handleChangeRowsPerPage,
    }), [page, count, itemsPerPage, handlePaginationChange, handleChangeRowsPerPage])

    const fields = useMemo<Field<any>[]>(() => [
        {
            sortable: false,
            label: "ID",
            name: "request",
            renderFunc: (field, item) => item.id,
        },
        {
            sortable: true,
            label: t('date'),
            name: "date",
            renderFunc: (field, item) => {
                const dateStr = new Date(item.date).toLocaleString()
                return dateStr.substring(0, dateStr.length - 3) + ' h'
            },
        },
        {
            sortable: false,
            label: t("codeSample"),
            name: "request",
            renderFunc: (field, item) => t(item.codeSample)
        },
        {
            sortable: true,
            label: t("state"),
            name: "state",
            renderFunc: (field, item) => t(item.status)
        },
        {
            searchable: true,
            sortable: false,
            label: t('bioarrayCode'),
            name: 'bioarrayCode',
            renderFunc: (field, item) => item.newID || item.idOdoo || '' ,
        },
    ], [t])

    const actions = useMemo<Actions<RequestView>>(() => ({
        actionsColumn: t('Actions'),
        items: [
            {
                handler: (r)=>{
                    setToDelete(r)
                    setModalDelete(true)},
                hidden: showDeleteButton,
                icon: deleteIcon,
                label: t('Delete'),
            },
        ],
    }), [t, deleteTest, showDeleteButton])

    const sortable = useMemo<Sort<RequestView>>(() => ({
        name: sort.field,
        direction: sort.desc ? 'desc' : 'asc',
        handleSort,
    }), [sort, handleSort])

    const search = useMemo<Search<Client2RequestQuery>>(() => ({
        searchValues: [{
            name: 'patient',
            label: t('search') + ' ' + t('byPatient'),
            value: searchQuery,
        }],
        handleSearch,
    }), [t, searchQuery, handleSearch])

    const handleCloseModal = ()=> {setModalDelete(false)}

    const handleDelete = () => {
        setModalDelete(false)
        toDelete && deleteTest(toDelete)
    }

    return (
        <>
            {!isLoading ? (
                
                <div>
                <AppTable
                    styleHeader={{ color: COLOR_HOME }}
                    actions={actions}
                    fields={fields}
                    items={paginatedItems}
                    rowKeyField={'id'}
                    pager={pager}
                    sort={sortable}
                    search={search}
                />

            <Modal
              open={modalDelete}
              onClose={handleCloseModal}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box className={styles.modal}>
                <form onSubmit={handleDelete}>
                    <Box mt={2} textAlign={'justify'}>
                    {t('deletecConfirmation')}
                    </Box>
                    <FormActions actions={actionsDelete} message={t('confirm')} />
                </form>
                </Box>
            </Modal>
            </div>
            ) : (<LoadingSpinner className={style.loadingSpinner} />

            )} 
        </>
    )
}